/**
 * @file index.tsx
 *
 * @fileoverview Renders the job search split view.
 */
import React from 'react';
import { HeroSimpleCenteredParagraph } from '~/jobiqo/types/types';
import styles from './heroSimpleCentered.module.scss';
import Image from '~/jobiqo/components/Image';
import { RowButtons } from '../RowButtons';
import { Text } from '~/vcl/atoms/13-text/03-text';
import { Box } from '~/vcl/atoms/07-box';
import { HeroRow } from '~/vcl/organisms/04-landingpage/02-hero-row';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { Main } from '~/vcl/atoms/06-layout/09-main';
import { getFrontendTheme } from '~/jobiqo/utils/getFrontend';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';

interface HeroSimpleCenteredRowProps {
  /**
   * The row to display.
   */
  rowBanner: HeroSimpleCenteredParagraph;
  /**
   * The index of this row. Used to detect if its the first row on the page.
   */
  index: number;
  /**
   * The max container size.
   */
  maxContainerSize?: 'small' | 'medium' | 'large' | 'xLarge';
}

export function HeroSimpleCenteredRow({
  rowBanner,
  index,
  maxContainerSize,
}: HeroSimpleCenteredRowProps) {
  const {
    image: { url, alt, title, caption },
    button1,
    button2,
    spacing,
    subText,
    h1,
    color,
    background,
    overlayTransparency,
  } = rowBanner;

  const frontendConfig = useFrontendConfig();
  const frontendTheme = getFrontendTheme(frontendConfig);
  const effectiveOverlayTransparency = overlayTransparency ?? 100;
  // Convert configured transparency to CSS opacity for the dark overlay layer.
  const overlayOpacity = 1 - effectiveOverlayTransparency / 100;

  return (
    <Box
      paddingTop={0}
      marginTop={0}
      backgroundColorOverride={
        background ? `var(--color-${background})` : `var(--color-white)`
      }
      boxShadow="none"
      borderRadius="0px"
      className="jd-lp-hero-simple-centered-row"
    >
      <div
        className={`${styles.wrapper} jd-lp-hero-simple-centered-wrapper`}
        style={{
          backgroundColor: frontendTheme.LANDING_PAGES.HERO_SIMPLE_CENTERED
            .IMAGE_BG
            ? frontendTheme.LANDING_PAGES.HERO_SIMPLE_CENTERED.IMAGE_BG
            : 'transparent',
          padding: frontendTheme.LANDING_PAGES.HERO_SIMPLE_CENTERED
            .IMAGE_PADDING
            ? frontendTheme.LANDING_PAGES.HERO_SIMPLE_CENTERED.IMAGE_PADDING
            : '0px',
        }}
      >
        <HeroRow
          image={
            <div
              className={styles.imageOverlay}
              style={
                { '--overlay-opacity': overlayOpacity } as React.CSSProperties
              }
            >
              <Image
                src={url}
                alt={alt ?? ''}
                title={title ?? ''}
                width={null}
                height={null}
                priority={index === 0}
                {...(caption && {
                  caption,
                })}
              />
            </div>
          }
        >
          <Spacing
            paddingTop={[0, spacing ?? 12]}
            paddingBottom={[0, spacing ?? 12]}
          >
            <Main {...(maxContainerSize ? { size: maxContainerSize } : null)}>
              <Text
                textAlign="center"
                tag="h1"
                fontSize={[6, 8]}
                lineHeight={[6, 7]}
              >
                {h1}
              </Text>
              <Spacing size="large" />
              <Text
                textAlign="center"
                color="gray6"
                fontSize={
                  frontendTheme.LANDING_PAGES.HERO_SIMPLE_CENTERED
                    .SUBTITLE_FONT_SIZE
                }
                lineHeight={
                  frontendTheme.LANDING_PAGES.HERO_SIMPLE_CENTERED
                    .SUBTITLE_LINE_HEIGHT
                }
              >
                {subText}
              </Text>
              <Spacing size="large" />
              <RowButtons
                button1={button1}
                button2={button2}
                buttonStyle={color as any}
              />
            </Main>
          </Spacing>
        </HeroRow>
      </div>
    </Box>
  );
}
export default HeroSimpleCenteredRow;
